import { foldDigitsToAscii } from './digits';

/**
 * Arabic normalization for **client-side matching only** — the vocabulary
 * picker's filter, in-page find, and client-side sort keys. Nothing displayed
 * is ever rewritten; only what is compared.
 *
 * There is **no ZWNJ rule in Arabic** — that is a Persian concern and must not
 * be carried over.
 *
 * This is the one implementation in the repo. Its server-side twin lives in
 * `backend-shared-logic`, and the two must agree: alef folding, ya folding,
 * ta-marbuta folding, tashkeel and tatweel stripped, Arabic-Indic digits folded
 * to ASCII.
 */

/** Harakat and the other combining marks Arabic input carries. */
const TASHKEEL = /[ً-ٰٟۖ-ۭ]/g;
/** Tatweel — a stretch character, never a letter. */
const TATWEEL = /ـ/g;
const ALEF_VARIANTS = /[آأإٱ]/g;
const YA_VARIANTS = /[ىي]/g;
const TA_MARBUTA = /ة/g;

export function normalizeArabic(input: string): string {
  return foldDigitsToAscii(
    input
      .normalize('NFC')
      .replace(TASHKEEL, '')
      .replace(TATWEEL, '')
      .replace(ALEF_VARIANTS, 'ا')
      .replace(YA_VARIANTS, 'ي')
      .replace(TA_MARBUTA, 'ه'),
  )
    .toLowerCase()
    .trim();
}

/** Substring match over the normalized forms of both sides. */
export function matchesQuery(candidate: string, query: string): boolean {
  const normalizedQuery = normalizeArabic(query);
  if (normalizedQuery.length === 0) return true;
  return normalizeArabic(candidate).includes(normalizedQuery);
}
